import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { menuInfo } from "@shared/type/CommonType";
import { ColumnConfig } from "@shared/components/Grid/types";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/Grid/SnGrid";
import SnTab from "@shared/components/SnTab";
import SnMemoBox from "@shared/components/SnMemoBox";
import SnButton from "@shared/components/SnButton";
import SnLabel from "@shared/components/SnLabel";
import SnTextBox from "@shared/components/SnTextBox";
import SnCheckBox from "@shared/components/SnCheckBox";
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import { copyText } from "@shared/utils/ClipboardUtils";
import { StyleSettings } from "../../../../../styles/StyleSettings";
import {
  clearProfilerLogs,
  fetchProfilerLogs,
  ProfilerLogs,
  fetchProfilerStatus,
  resolveBaseUrl,
  switchProfiler,
} from "./profilerApi";
import { ProfilerBridge } from "./profilerBridge";
import {
  extractParams,
  parseLogEntry,
  ProfilerLogRow,
  ProfilerParamRow,
} from "./profilerUtils";

interface Props {
  Menuprops?: menuInfo;
}

const MENU_CODE = "SN_SYS_BAS_REP_002";
const MAX_ROWS = 5000; // 화면에 보관하는 최대 로그 수
const MAX_CONSECUTIVE_ERRORS = 3; // 연속 실패 시 폴링 자동 중단
const POLL_INTERVALS = [
  { label: "1초", value: 1000 },
  { label: "2초", value: 2000 },
  { label: "5초", value: 5000 },
  { label: "10초", value: 10000 },
];

const DML_STYLE: Record<string, React.CSSProperties> = {
  INSERT: { color: "#1d4ed8" },
  UPDATE: { color: "#b45309" },
  DELETE: { color: "#b91c1c", fontWeight: "bold" },
  TRUNCATE: { color: "#b91c1c", fontWeight: "bold" },
};

/** 하드코딩 컬럼 정의 (메뉴 그리드 설정 없이 동작하도록) */
const col = (
  cd_grid: string,
  cd_column: string,
  nm_column: string,
  qt_width: number,
  no_seq: number
): ColumnConfig => ({
  cd_firm: "",
  cd_menu: MENU_CODE,
  cd_grid,
  cd_user: "",
  cd_column,
  nm_column,
  tp_column: "SnTextBox",
  fg_visible: "Y",
  no_seq,
  qt_width,
  yn_required: "N",
  yn_allowsorting: "Y",
  dc_editcon: "1 == 1",
});

const LOG_COLUMNS: ColumnConfig[] = [
  col("grid1", "tm_log", "시각", 110, 2),
  col("grid1", "tp_sql", "구분", 90, 3),
  col("grid1", "nm_table", "테이블", 220, 4),
  col("grid1", "dc_sql", "SQL", 900, 5),
];

const PARAM_COLUMNS: ColumnConfig[] = [
  col("grid3", "nm_column", "컬럼", 160, 2),
  col("grid3", "cd_oper", "연산", 70, 3),
  col("grid3", "dc_value", "값", 300, 4),
];

/**
 * 시스템관리 > 프로필러
 * 백엔드 p6spy 로그 버퍼를 주기적으로 가져와 SQL 실행 내역을 실시간으로 보여준다.
 */
const SN_SYS_BAS_REP_002 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region variable
  const { userInfo } = global();
  const serverRef = useRef<any>(null);
  const tenantRef = useRef<any>(null);
  const normalGridRef = useRef<any>(null);
  const systemGridRef = useRef<any>(null);
  const accessGridRef = useRef<any>(null);
  const paramGridRef = useRef<any>(null);
  const filterRef = useRef<any>(null);
  const copiedTimerRef = useRef<number | null>(null);

  // 폴링 콜백에서 최신 상태를 읽기 위한 ref
  const tracingRef = useRef(false);
  const pollingRef = useRef(false);
  const seenRef = useRef<Set<string>>(new Set());
  const seqRef = useRef(0);
  const errorCountRef = useRef(0);
  const ipOnlyRef = useRef(false);
  const autoScrollRef = useRef(true);
  const activeGridTabRef = useRef("1");
  //#endregion

  //#region state
  const [tracing, setTracing] = useState(false);
  // 서버 추적 스위치의 실제 상태 (null = 아직 확인 안 됨)
  const [serverEnabled, setServerEnabled] = useState<boolean | null>(null);
  const [rows, setRows] = useState<ProfilerLogRow[]>([]);
  const [detail, setDetail] = useState<ProfilerLogRow | null>(null);
  const [intervalMs, setIntervalMs] = useState(2000);
  const [lastReceived, setLastReceived] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  // 그리드 상단 검색어 (SQL 원문 / 테이블명 / 구분 대상, 공백으로 나눈 단어 AND 검색)
  const [filterText, setFilterText] = useState("");
  // SQL 복사 버튼 피드백 ("" | "복사됨" | "선택영역 복사됨")
  const [copied, setCopied] = useState("");
  //#endregion

  //#region panel 사이즈
  const cd_menu = Menuprops?.cd_menu || MENU_CODE;
  const defaultSize = { panel1width: "65%", panel2width: "34%" };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);
  //#endregion

  const allNormalRows = useMemo(
    () => rows.filter((r) => r.fg_sys === "N"),
    [rows]
  );
  const systemRows = useMemo(
    () => rows.filter((r) => r.fg_sys === "Y"),
    [rows]
  );
  const accessRows = useMemo(
    () => rows.filter((r) => r.fg_sys === "A"),
    [rows]
  );

  // 검색은 일반쿼리 탭에만 적용한다 (시스템/액세스 탭은 항상 전체 표시)
  const normalRows = useMemo(() => {
    const words = filterText
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w !== "");
    if (words.length === 0) return allNormalRows;
    return allNormalRows.filter((r) => {
      const hay = `${r.tp_sql} ${r.nm_table} ${r.txt_sql}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [allNormalRows, filterText]);
  const isFiltering = filterText.trim() !== "";
  const paramRows = useMemo<ProfilerParamRow[]>(
    () => (detail ? extractParams(detail.txt_sql) : []),
    [detail]
  );

  const getTarget = () => ({
    baseUrl: resolveBaseUrl(serverRef.current?.value ?? ""),
    tenantId: (tenantRef.current?.value ?? "").trim() || "DEFAULT",
  });

  const failed = (e: any) => {
    setError(e?.message || "요청 처리 중 오류가 발생했습니다.");
  };

  //#region 전송 계층 (현재 서버 = 직접 fetch, 다른 사이트 = 연결창 경유)
  const bridgeRef = useRef<ProfilerBridge | null>(null);
  const getBridge = () => {
    if (!bridgeRef.current) bridgeRef.current = new ProfilerBridge();
    return bridgeRef.current;
  };
  const isRemote = () => getTarget().baseUrl !== "";

  // 컴포넌트가 사라지면 연결창도 닫는다
  useEffect(() => () => bridgeRef.current?.dispose(), []);

  // 현재 서버 대상이면 진입 시 서버 스위치 상태를 한 번 조회해 표시등을 맞춘다
  useEffect(() => {
    if (isRemote()) return;
    const tenant = tenantRef.current?.value || userInfo?.tenant_id || "DEFAULT";
    fetchProfilerStatus("", tenant)
      .then((st) => setServerEnabled(st.enabled))
      .catch(() => {}); // 옛 백엔드(404) 등은 미확인으로 둔다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 다른 사이트면 연결창을 열어 두고(이미 열려 있으면 재사용) 대상 정보를 돌려준다 */
  const ensureTransport = async () => {
    const target = getTarget();
    if (target.baseUrl !== "") await getBridge().open(target.baseUrl);
    return target;
  };

  const apiSwitch = async (on: boolean): Promise<string> => {
    const { baseUrl, tenantId } = await ensureTransport();
    return baseUrl
      ? getBridge().call<string>("switch", tenantId, {
          status: on ? "on" : "off",
        })
      : switchProfiler("", tenantId, on);
  };

  const apiLogs = async (ipOnly: boolean): Promise<ProfilerLogs> => {
    const { baseUrl, tenantId } = getTarget();
    return baseUrl
      ? getBridge().call<ProfilerLogs>(
          ipOnly ? "getIpLogs" : "getLogs",
          tenantId
        )
      : fetchProfilerLogs("", tenantId, ipOnly);
  };

  const apiClear = async (): Promise<void> => {
    const { baseUrl, tenantId } = await ensureTransport();
    if (baseUrl) await getBridge().call("clearLog", tenantId);
    else await clearProfilerLogs("", tenantId);
  };

  const apiStatus = async () => {
    const { baseUrl, tenantId } = await ensureTransport();
    return baseUrl
      ? getBridge().call<{ enabled: boolean; logCount: number }>(
          "status",
          tenantId
        )
      : fetchProfilerStatus("", tenantId);
  };

  /** 추적 중이 아니면 연결창을 붙잡고 있을 이유가 없다 */
  const releaseBridgeIfIdle = () => {
    if (!tracingRef.current) bridgeRef.current?.close();
  };
  //#endregion

  const scrollToBottom = () => {
    if (!autoScrollRef.current) return;
    const grid =
      activeGridTabRef.current === "1"
        ? normalGridRef
        : activeGridTabRef.current === "2"
          ? systemGridRef
          : accessGridRef;
    // SnGrid 가 rowData 를 반영한 뒤에 스크롤해야 한다
    setTimeout(() => {
      const api = grid.current?.api;
      if (!api) return;
      const count = api.getDisplayedRowCount();
      if (count > 0) api.ensureIndexVisible(count - 1, "bottom");
    }, 80);
  };

  /** 서버 버퍼를 한 번 읽어 새 로그만 덧붙인다 */
  const pollOnce = useCallback(async () => {
    if (pollingRef.current) return; // 이전 요청이 아직 진행 중
    pollingRef.current = true;
    try {
      // 사용자가 연결창을 직접 닫았으면 바로 중단
      if (isRemote() && !getBridge().isOpen()) {
        tracingRef.current = false;
        setTracing(false);
        setMessage("연결창이 닫혀 추적을 중단했습니다.");
        return;
      }
      const result: any = await apiLogs(ipOnlyRef.current);
      // 옛 백엔드/옛 연결창은 배열만 돌려주므로 두 형식을 모두 받는다
      const logs: string[] = Array.isArray(result)
        ? result
        : Array.isArray(result?.logs)
          ? result.logs
          : [];
      const enabled: boolean | null =
        typeof result?.enabled === "boolean" ? result.enabled : null;

      // 응답에 실린 서버 스위치 상태로 표시등을 맞추고, 다른 곳에서 꺼졌으면 수집을 멈춘다
      if (enabled !== null) {
        setServerEnabled(enabled);
        if (!enabled && tracingRef.current) {
          tracingRef.current = false;
          setTracing(false);
          setMessage("서버 추적이 다른 곳에서 종료되어 수집을 중단했습니다.");
          releaseBridgeIfIdle();
        }
      }

      const fresh: ProfilerLogRow[] = [];
      for (const raw of logs) {
        if (seenRef.current.has(raw)) continue;
        seenRef.current.add(raw);
        seqRef.current += 1;
        fresh.push(parseLogEntry(raw, seqRef.current));
      }

      if (fresh.length > 0) {
        // IP 별 버퍼가 이어붙어 오므로 시각 기준으로 정렬해 붙인다
        fresh.sort((a, b) => a.tm_full.localeCompare(b.tm_full));
        setRows((prev) => {
          const next = prev.concat(fresh);
          return next.length > MAX_ROWS
            ? next.slice(next.length - MAX_ROWS)
            : next;
        });
        scrollToBottom();
      }

      // 서버 버퍼는 IP 당 1000건에서 오래된 것부터 밀려나므로 중복키 집합도 같이 정리
      if (seenRef.current.size > MAX_ROWS * 4) {
        seenRef.current = new Set(logs);
      }

      setLastReceived(new Date().toLocaleTimeString());
      setError("");
      errorCountRef.current = 0;
    } catch (e: any) {
      errorCountRef.current += 1;
      failed(e);
      if (
        errorCountRef.current >= MAX_CONSECUTIVE_ERRORS &&
        tracingRef.current
      ) {
        tracingRef.current = false;
        setTracing(false);
        setMessage("연속 실패로 추적을 중단했습니다.");
      }
    } finally {
      pollingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 폴링 루프
  useEffect(() => {
    if (!tracing) return;
    pollOnce();
    const timer = window.setInterval(pollOnce, intervalMs);
    return () => window.clearInterval(timer);
  }, [tracing, intervalMs, pollOnce]);

  //#region 제어 버튼
  /** 서버의 실제 스위치 상태를 물어본다. 화면 표시등은 내가 켰는지 여부라 서버와 다를 수 있다 */
  const onStatus = async () => {
    setError("");
    try {
      const st = await apiStatus();
      setServerEnabled(st.enabled);
      const { tenantId } = getTarget();
      setMessage(
        `[${tenantId}] 서버 추적 ${st.enabled ? "ON" : "OFF"} / 버퍼 ${st.logCount.toLocaleString()}건`
      );
    } catch (e) {
      failed(e);
    } finally {
      releaseBridgeIfIdle();
    }
  };

  const onStart = async () => {
    setError("");
    try {
      const rtn = await apiSwitch(true);
      setServerEnabled(true);
      setMessage(rtn.replace(/=/g, "").trim());
      tracingRef.current = true;
      setTracing(true);
    } catch (e) {
      failed(e);
      releaseBridgeIfIdle();
    }
  };

  const onStop = async () => {
    setError("");
    tracingRef.current = false;
    setTracing(false);
    try {
      const rtn = await apiSwitch(false);
      setServerEnabled(false);
      setMessage(rtn.replace(/=/g, "").trim());
    } catch (e) {
      failed(e);
    } finally {
      // 다른 사이트였다면 요청 대행 창도 함께 닫는다
      releaseBridgeIfIdle();
    }
  };

  const clearScreen = () => {
    setRows([]);
    setDetail(null);
    seenRef.current = new Set();
    seqRef.current = 0;
  };

  const onClear = async () => {
    setError("");
    try {
      await apiClear();
      clearScreen();
      setMessage("로그를 초기화했습니다.");
    } catch (e) {
      failed(e);
    } finally {
      releaseBridgeIfIdle();
    }
  };
  //#endregion

  const onFocusedRowChanged = useCallback((row: any | null) => {
    if (row) {
      setDetail(row);
      setCopied("");
    }
  }, []);

  /** SQL 복사: 선택된 행의 SQL 원문 전체를 클립보드에 복사한다 */
  const onCopySql = async () => {
    if (!detail) return;
    const ok = await copyText(detail.txt_sql);
    if (!ok) {
      setError("클립보드 복사에 실패했습니다.");
      return;
    }
    setCopied("복사됨");
    if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = window.setTimeout(() => setCopied(""), 1500);
  };

  // 화면을 떠날 때 피드백 타이머 정리
  useEffect(
    () => () => {
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    },
    []
  );

  const gridOptions = useMemo(
    () => ({
      getRowStyle: (params: any) => DML_STYLE[params.data?.tp_sql] ?? undefined,
    }),
    []
  );

  const renderLogGrid = (
    gridRef: React.MutableRefObject<any>,
    seq: number,
    data: ProfilerLogRow[]
  ) => (
    <div className="h-full w-full flex flex-col">
      <SnGrid
        seq={seq}
        ref={gridRef}
        columnConfig={LOG_COLUMNS}
        rowData={data}
        isEditable={false}
        rowSelection="single"
        animateRows={false}
        gridOptions={gridOptions}
        onFocusedRowChanged={onFocusedRowChanged}
      />
    </div>
  );

  const statusColor =
    serverEnabled === null ? "#9ca3af" : serverEnabled ? "#22c55e" : "#ef4444";
  const statusText =
    serverEnabled === null ? "미확인" : serverEnabled ? "ON" : "OFF";
  const fieldStyle = { fontSize: StyleSettings.font };

  return (
    <div className="h-full flex flex-col gap-2">
      {/* 상단: 서버연결 / 제어 */}
      <div className="flex flex-row gap-2">
        <fieldset className="border border-gray-300 rounded-md px-3 pb-2 pt-1 min-w-[420px]">
          <legend className="px-1 font-bold" style={fieldStyle}>
            서버연결
          </legend>
          <div className="flex items-center space-x-2 mb-1">
            <SnLabel text="서버주소" type="FreeForm" />
            <div className="flex-1">
              <SnTextBox inputRef={serverRef} isReadOnly={tracing} />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <SnLabel text="Tenant ID" type="FreeForm" />
            <div className="flex-1">
              <SnTextBox
                inputRef={tenantRef}
                defaultValue={userInfo?.tenant_id || "DEFAULT"}
                isReadOnly={tracing}
              />
            </div>
          </div>
          <div className="text-gray-500 mt-1" style={fieldStyle}>
            사이트 주소를 넣으면 그 사이트에 요청 대행용 작은 연결창이 열립니다(
            <span className="text-red-600 font-bold">
              해당 사이트에 주소로 발급된 SESSION 쿠키가 남아 있어야 함
            </span>
            , 추적종료 시 자동 닫힘)
          </div>
        </fieldset>

        <fieldset className="border border-gray-300 rounded-md px-3 pb-2 pt-1 flex-1">
          <legend className="px-1 font-bold" style={fieldStyle}>
            제어
          </legend>
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
            <SnButton text="상태확인" onClick={onStatus} />
            <SnButton text="추적시작" onClick={onStart} disabled={tracing} />
            <SnButton text="추적종료" onClick={onStop} disabled={!tracing} />
            <SnButton text="로그초기화" onClick={onClear} />

            <div className="flex items-center gap-1" style={fieldStyle}>
              <span>상태</span>
              <span
                className="inline-block w-3 h-3 rounded-full"
                style={{ backgroundColor: statusColor }}
              />
              <span style={{ color: statusColor, fontWeight: "bold" }}>
                {statusText}
                {tracing && " (수집중)"}
              </span>
            </div>
          </div>
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1">
            <div className="flex items-center gap-1" style={fieldStyle}>
              <span>주기</span>
              <select
                className="border border-gray-300 rounded px-1 h-[26px]"
                style={fieldStyle}
                value={intervalMs}
                onChange={(e) => setIntervalMs(Number(e.target.value))}
              >
                {POLL_INTERVALS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <SnCheckBox
              label="내 IP만"
              defaultValue="N"
              onValueChanged={(v) => {
                ipOnlyRef.current = v === "Y";
              }}
            />
            <SnCheckBox
              label="자동스크롤"
              defaultValue="Y"
              onValueChanged={(v) => {
                autoScrollRef.current = v === "Y";
              }}
            />
          </div>
          <div
            className="flex items-center flex-wrap gap-x-4 mt-1 text-gray-600"
            style={fieldStyle}
          >
            <span>
              일반 {allNormalRows.length.toLocaleString()}건 / 시스템{" "}
              {systemRows.length.toLocaleString()}건 / 액세스{" "}
              {accessRows.length.toLocaleString()}건
            </span>
            {lastReceived && <span>마지막 수신 {lastReceived}</span>}
            {message && <span>{message}</span>}
            {error && <span className="text-red-600 font-bold">{error}</span>}
          </div>
        </fieldset>
      </div>

      {/* 본문 */}
      <div className="flex-1 min-h-0 flex flex-row gap-2">
        {/* 좌측: 로그 그리드 */}
        <SnResizable
          direction={"width"}
          seq={1}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel1width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={300}
        >
          <div className="w-full h-full flex flex-col">
            <SnTab
              titles={["일반쿼리", "시스템쿼리", "액세스쿼리"]}
              width={"100%"}
              onTabChanged={(index) => {
                activeGridTabRef.current = index;
              }}
              header={[
                // 일반쿼리 탭 헤더: 검색 (이 탭에만 적용)
                <div className="flex items-center space-x-2 pr-2 w-[420px]">
                  <SnLabel text="검색" type="FreeForm" />
                  <div className="flex-1">
                    <SnTextBox
                      inputRef={filterRef}
                      onValueChanged={(v) => setFilterText(v ?? "")}
                    />
                  </div>
                  <span
                    className="text-gray-500 whitespace-nowrap"
                    style={fieldStyle}
                  >
                    {isFiltering
                      ? `${normalRows.length.toLocaleString()} / ${allNormalRows.length.toLocaleString()}건`
                      : "공백은 AND"}
                  </span>
                </div>,
                <div />,
                <div />,
              ]}
              body={[
                renderLogGrid(normalGridRef, 1, normalRows),
                renderLogGrid(systemGridRef, 2, systemRows),
                renderLogGrid(accessGridRef, 3, accessRows),
              ]}
            />
          </div>
        </SnResizable>

        {/* 우측: 상세 */}
        <SnResizable
          direction={"width"}
          seq={2}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel2width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={200}
        >
          <div className="w-full h-full flex flex-col">
            <SnTab
              titles={["SQL", "Param"]}
              width={"100%"}
              header={[
                <div
                  className="flex items-center gap-2 pr-2 text-gray-600"
                  style={fieldStyle}
                >
                  <span>
                    {detail
                      ? `${detail.tm_log} ${detail.tp_sql}`
                      : "행을 선택하세요"}
                  </span>
                  {copied && (
                    <span className="text-green-600 font-bold">{copied}</span>
                  )}
                  <SnButton
                    text="복사"
                    onClick={onCopySql}
                    disabled={!detail}
                  />
                </div>,
                <div className="pr-2 text-gray-600" style={fieldStyle}>
                  SQL 리터럴 기준 추출
                </div>,
              ]}
              body={[
                <div className="h-full w-full flex flex-col">
                  <SnMemoBox
                    defaultValue={detail?.txt_sql ?? ""}
                    isReadOnly={true}
                  />
                </div>,
                <div className="h-full w-full flex flex-col">
                  <SnGrid
                    seq={3}
                    ref={paramGridRef}
                    columnConfig={PARAM_COLUMNS}
                    rowData={paramRows}
                    isEditable={false}
                    rowSelection="single"
                    animateRows={false}
                  />
                </div>,
              ]}
            />
          </div>
        </SnResizable>
      </div>
    </div>
  );
});

export default SN_SYS_BAS_REP_002;
